import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGuestTitle } from '../lib/guestRoute';
import { landingAPI } from '../lib/api';
import { Star, ExternalLink, Loader2, Crown } from 'lucide-react';
import { CardScanActions } from '../components/shared/CardScanActions';
import { Button } from '../components/ui/button';
import LandingMediaPlayer from '../components/LandingMediaPlayer';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { parseUtc } from '../lib/datetime';

const PLATFORM_LABELS = {
    google_reviews: 'Google',
    facebook: 'Facebook',
    instagram: 'Instagram',
    trustpilot: 'Trustpilot',
    yelp: 'Yelp',
    tripadvisor: 'TripAdvisor'
};

const PLATFORM_COLORS = {
    google_reviews: '#4285F4',
    facebook: '#1877F2',
    instagram: '#E4405F',
    trustpilot: '#00B67A',
    yelp: '#D32323',
    tripadvisor: '#34E0A1'
};

const CLOCK_COLORS = {
    red: '#EF4444',
    blue: '#3B82F6',
    green: '#22C55E',
    yellow: '#EAB308',
    orange: '#F97316',
    white: '#FFFFFF',
    fuchsia: '#D946EF'
};

const CountdownRing = ({ seconds, totalSeconds, platformLabel, clockColor }) => {
    const radius = 55;
    const stroke = 5;
    const normalizedRadius = radius - stroke;
    const circumference = normalizedRadius * 2 * Math.PI;
    const progress = seconds / totalSeconds;
    const strokeDashoffset = circumference * (1 - progress);

    return (
        <div className="flex flex-col items-center gap-2" data-testid="countdown-ring">
            <p 
                className="text-sm font-medium tracking-wide animate-pulse"
                style={{ color: clockColor }}
            >
                Redirecting to {platformLabel} in...
            </p>
            <div className="relative" style={{ width: radius * 2, height: radius * 2 }}>
                {/* Outer glow */}
                <div 
                    className="absolute inset-0 rounded-full blur-xl opacity-30"
                    style={{ backgroundColor: clockColor }}
                />
                {/* SVG Ring */}
                <svg
                    height={radius * 2}
                    width={radius * 2}
                    className="transform -rotate-90 relative z-10"
                >
                    {/* Background track */}
                    <circle
                        stroke="rgba(255,255,255,0.08)"
                        fill="transparent"
                        strokeWidth={stroke}
                        r={normalizedRadius}
                        cx={radius}
                        cy={radius}
                    />
                    {/* Animated progress ring */}
                    <circle
                        stroke={clockColor}
                        fill="transparent"
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={circumference + ' ' + circumference}
                        style={{ 
                            strokeDashoffset,
                            transition: 'stroke-dashoffset 0.15s ease-out',
                            filter: `drop-shadow(0 0 8px ${clockColor})`
                        }}
                        r={normalizedRadius}
                        cx={radius}
                        cy={radius}
                    />
                    {/* Subtle tick marks around the ring */}
                    {Array.from({ length: 60 }).map((_, i) => {
                        const angle = (i * 6 - 90) * (Math.PI / 180);
                        const isMajor = i % 5 === 0;
                        const outerR = normalizedRadius + stroke + 6;
                        const innerR = outerR - (isMajor ? 6 : 3);
                        return (
                            <line
                                key={i}
                                x1={radius + outerR * Math.cos(angle)}
                                y1={radius + outerR * Math.sin(angle)}
                                x2={radius + innerR * Math.cos(angle)}
                                y2={radius + innerR * Math.sin(angle)}
                                stroke={isMajor ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)'}
                                strokeWidth={isMajor ? 1.5 : 0.5}
                            />
                        );
                    })}
                </svg>
                {/* Center text */}
                <div className="absolute inset-0 flex items-center justify-center z-20">
                    <span 
                        className="text-3xl font-bold tabular-nums"
                        style={{ 
                            color: clockColor,
                            textShadow: `0 0 20px ${clockColor}60`,
                            fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace"
                        }}
                    >
                        {seconds}
                    </span>
                </div>
            </div>
        </div>
    );
};

const LandingPage = () => {
    const { cardId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    // Guest tab title = the client's business name, never the portal's.
    useGuestTitle(data?.organization_name);
    const [redirecting, setRedirecting] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const timerRef = useRef(null);
    const countdownRef = useRef(null);
    const hasRecordedTap = useRef(false);
    const [vipSession, setVipSession] = useState(null);
    const [vipDelivered, setVipDelivered] = useState(null);
    const pageLoadTimeRef = useRef(Date.now());

    useEffect(() => {
        // NEO Master Manager Card + DELDEMO routing — check first; if this is a
        // master-manager card on a NEO-enabled location, bypass landing/ad/review
        // entirely and redirect to NEO login. If it's a demo card, redirect to
        // the dedicated persistent demo voucher page.
        fetch(`${window.location.origin}/api/neo/scan-route/${cardId}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (!d) return;
                if (d.target === 'neo') {
                    navigate(`/manager/${cardId}`, { replace: true });
                } else if (d.target === 'demo') {
                    navigate(`/demo/${cardId}`, { replace: true });
                } else if (d.target === 'assistant') {
                    navigate(`/assistant/${cardId}`, { replace: true });
                } else if (d.target === 'assistant_staff') {
                    navigate(`/assistant/staff/${cardId}`, { replace: true });
                }
            })
            .catch(() => {});
        loadLandingData();
        // Check for VIP session
        fetch(`${window.location.origin}/api/vouchers/vip/check/${cardId}`)
            .then(r => r.ok ? r.json() : { active: false })
            .then(d => { if (d.active) setVipSession(d); })
            .catch(() => {});
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [cardId]);

    // Staff-parity: if the manager session was just locked, flash the same neutral
    // "Session locked for your security" toast on arrival. Delayed 50ms so the
    // Toaster is mounted before sonner publishes (mirrors StaffMenu.jsx pattern).
    useEffect(() => {
        if (location.state?.sessionLocked) {
            const id = setTimeout(() => {
                toast(t('manager.sessionLocked', 'Session locked for your security'), {
                    id: 'session-locked',
                    duration: 2000,
                });
            }, 50);
            // Clear the navigation state so a browser refresh doesn't re-fire.
            window.history.replaceState({}, '');
            return () => clearTimeout(id);
        }
    }, [location.state, t]);

    const deliverVip = async () => {
        if (!vipSession) return;
        try {
            const res = await fetch(`${window.location.origin}/api/vouchers/vip/deliver/${vipSession.session_id}`, { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                setVipDelivered(data);
                setVipSession(null);
            }
        } catch { /* silent */ }
    };

    // Countdown timer
    useEffect(() => {
        if (data && !hasRecordedTap.current && data.landing_auto_redirect !== false) {
            const totalSeconds = data.landing_redirect_delay || 5;
            setCountdown(totalSeconds);

            countdownRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownRef.current);
                        handleRedirect();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => {
                if (countdownRef.current) clearInterval(countdownRef.current);
            };
        }
    }, [data]);

    const loadLandingData = async () => {
        try {
            setLoading(true);
            const response = await landingAPI.getData(cardId);
            setData(response.data);
        } catch (err) {
            console.error('Failed to load landing page:', err);
            if (err.response?.status === 404) {
                setError('This card does not have a landing page configured.');
            } else {
                setError('Unable to load landing page. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRedirect = useCallback(async () => {
        if (hasRecordedTap.current) return;
        hasRecordedTap.current = true;
        setRedirecting(true);
        if (countdownRef.current) clearInterval(countdownRef.current);
        setCountdown(0);

        // Calculate dwell time (seconds on page before redirect)
        const dwellSeconds = (Date.now() - pageLoadTimeRef.current) / 1000;

        try {
            const response = await landingAPI.recordTap(cardId, dwellSeconds);
            window.location.href = response.data.redirect_url;
        } catch (err) {
            console.error('Failed to record tap:', err);
            if (data?.redirect_url) {
                window.location.href = data.redirect_url;
            }
        }
    }, [cardId, data]);

    // VIP Delivery Display
    if (vipDelivered) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="vip-customer-delivery">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto">
                        <Crown className="w-8 h-8 text-red-400" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-semibold text-white">VIP Voucher</h2>
                        <p className="text-4xl font-bold text-emerald-400">${vipDelivered.value}</p>
                        <p className="text-lg font-mono text-white tracking-wider mt-2">{vipDelivered.code}</p>
                    </div>
                    {vipDelivered.message && (
                        <p className="text-sm text-slate-400 italic">{vipDelivered.message}</p>
                    )}
                    <div className="bg-slate-900 rounded-lg p-3 text-left space-y-1 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>{t('voucher.validUntil')}</span>
                            <span className="text-slate-200">{parseUtc(vipDelivered.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        {vipDelivered.organization_name && (
                            <div className="flex justify-between text-slate-400">
                                <span>{t('voucher.from')}</span>
                                <span className="text-slate-200">{vipDelivered.organization_name}</span>
                            </div>
                        )}
                    </div>
                    <a href={`/voucher/${vipDelivered.code}`} className="block w-full">
                        <Button data-testid="vip-save-photo-customer" variant="outline" className="w-full border-slate-700 text-white hover:bg-slate-800">
                            {t('voucher.saveToPhotos')}
                        </Button>
                    </a>
                    <CardScanActions cardId={cardId} className="pt-2" />
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto" />
                    <p className="text-slate-300 mt-4">Loading...</p>
                    <CardScanActions cardId={cardId} className="mt-6" />
                </div>
                <Toaster position="top-center" data-testid="landing-session-toaster" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Star className="w-8 h-8 text-red-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">Oops!</h1>
                    <p className="text-slate-300">{error}</p>
                    <CardScanActions cardId={cardId} className="mt-6" />
                </div>
                <Toaster position="top-center" data-testid="landing-session-toaster" />
            </div>
        );
    }

    const platformLabel = PLATFORM_LABELS[data?.platform] || 'Review';
    const platformColor = PLATFORM_COLORS[data?.platform] || '#10b981';
    const totalSeconds = data?.landing_redirect_delay || 5;
    const showClock = data?.countdown_clock_enabled !== false;
    const clockColor = CLOCK_COLORS[data?.countdown_clock_color] || CLOCK_COLORS.blue;
    const showText = data?.landing_show_text !== false;

    const logoSizeClasses = {
        small: 'max-h-16 md:max-h-20 max-w-[120px] md:max-w-[160px]',
        medium: 'max-h-28 md:max-h-32 max-w-[200px] md:max-w-[260px]',
        large: 'max-h-40 md:max-h-48 max-w-[280px] md:max-w-[360px]',
        very_large: 'max-h-56 md:max-h-64 max-w-[360px] md:max-w-[480px]'
    };
    const logoClass = logoSizeClasses[data?.landing_logo_size] || logoSizeClasses.medium;

    // Video feature flag check
    const showVideo = data?.landing_video_enabled && data?.media_type === 'video' && data?.media_video_url;

    return (
        <div 
            className="min-h-screen flex flex-col items-center justify-center px-5 py-6"
            style={{
                background: `linear-gradient(135deg, #0f172a 0%, #1e293b 50%, ${platformColor}22 100%)`
            }}
            data-testid="landing-page"
        >
            <div className="w-full max-w-lg text-center space-y-4">
                {/* Client Logo */}
                {data?.landing_logo_url && (
                    <div className="flex justify-center">
                        <img 
                            src={data.landing_logo_url} 
                            alt="Logo"
                            className={`${logoClass} object-contain rounded-lg`}
                            data-testid="landing-logo"
                        />
                    </div>
                )}

                {/* Video Media Player */}
                {showVideo && (
                    <div className="w-full" data-testid="landing-video-section">
                        <LandingMediaPlayer
                            videoId={data.media_video_id}
                            videoUrl={data.media_video_url}
                            posterUrl={data.media_video_poster_url}
                            fallbackImageUrl={data.media_video_fallback_image}
                            autoplay={data.media_autoplay}
                            loop={data.media_loop}
                            muted={data.media_muted}
                            skipEnabled={data.media_skip_enabled}
                            skipDelay={data.media_skip_delay}
                            onSkip={handleRedirect}
                            onVideoEnd={() => {
                                if (!data.media_loop) handleRedirect();
                            }}
                        />
                    </div>
                )}

                {/* Organization Name - only if present */}
                {data?.organization_name && (
                    <div>
                        <p className="text-slate-400 text-xs uppercase tracking-wider">{t('landingPage.welcomeTo')}</p>
                        <h2 className="text-xl font-bold text-white">{data.organization_name}</h2>
                        {data?.location_name && (
                            <p className="text-slate-400 text-sm">{data.location_name}</p>
                        )}
                    </div>
                )}

                {/* Custom Headline - controlled by show text toggle */}
                {showText && (data?.landing_headline || data?.landing_description) && (
                    <div className="space-y-2">
                        {data?.landing_headline && (
                            <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                                {data.landing_headline}
                            </h1>
                        )}
                        {data?.landing_description && (
                            <p className="text-base text-slate-300">
                                {data.landing_description}
                            </p>
                        )}
                    </div>
                )}

                {/* Countdown Timer OR CTA Button */}
                <div className="pt-2">
                    {countdown !== null && countdown > 0 && !redirecting ? (
                        <div className="space-y-4">
                            {showClock && (
                                <CountdownRing
                                    seconds={countdown}
                                    totalSeconds={totalSeconds}
                                    platformLabel={platformLabel}
                                    clockColor={clockColor}
                                />
                            )}
                            {!showClock && (
                                <p className="text-slate-300 text-sm">
                                    Redirecting to {platformLabel} in {countdown}s...
                                </p>
                            )}
                            <Button
                                onClick={handleRedirect}
                                size="lg"
                                className="w-full py-5 text-base font-semibold rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105"
                                style={{ backgroundColor: platformColor, color: 'white' }}
                                data-testid="landing-cta-button"
                            >
                                <Star className="w-5 h-5 mr-2" />
                                Leave a {platformLabel} Review Now
                                <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    ) : redirecting || countdown === 0 ? (
                        <div className="space-y-3">
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 
                                    className="w-8 h-8 animate-spin" 
                                    style={{ color: platformColor }} 
                                />
                                <p className="text-white text-base font-medium">
                                    Taking you to {platformLabel}...
                                </p>
                            </div>
                        </div>
                    ) : (
                        <Button
                            onClick={handleRedirect}
                            disabled={redirecting}
                            size="lg"
                            className="w-full py-5 text-base font-semibold rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105"
                            style={{ backgroundColor: platformColor, color: 'white' }}
                            data-testid="landing-cta-button"
                        >
                            <Star className="w-5 h-5 mr-2" />
                            Leave a {platformLabel} Review
                            <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                    )}
                </div>

                {/* Card scan footer actions — always visible, regardless of branding toggle */}
                <div className="pt-4 flex flex-col items-center gap-2">
                    <CardScanActions
                        cardId={cardId}
                        showPoweredBy={data?.show_yulontap_branding !== false}
                    />
                    {vipSession && (
                        <button
                            onClick={deliverVip}
                            data-testid="vip-customer-btn"
                            className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-8 py-2 rounded-full transition-colors shadow-lg shadow-red-600/30"
                        >
                            VIP
                        </button>
                    )}
                </div>
            </div>
            <Toaster position="top-center" data-testid="landing-session-toaster" />
        </div>
    );
};

export default LandingPage;
